import {
  Box,
  Card,
  Flex,
  Group,
  Image,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { ReactNode } from 'react';

interface SessionTypeCardProps {
  icon: string;
  title: string;
  description: string;
  isSelected: boolean;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  footer?: ReactNode;
  brandLogos?:
    | Array<{ url: string; isActive: boolean; name: string }>
    | string[];
}

export function SessionTypeCard({
  icon,
  title,
  description,
  isSelected,
  onClick,
  disabled = false,
  disabledReason,
  footer,
  brandLogos,
}: SessionTypeCardProps) {
  return (
    <Card
      withBorder
      p='md'
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s ease',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderColor:
          disabled && !isSelected
            ? 'var(--mantine-color-gray-4)'
            : isSelected
              ? 'var(--mantine-color-blue-6)'
              : undefined,
        borderWidth: isSelected ? 2 : 1,
        backgroundColor: isSelected
          ? 'var(--mantine-color-blue-0)'
          : disabled
            ? 'var(--mantine-color-gray-0)'
            : undefined,
        opacity: disabled && !isSelected ? 0.7 : 1,
      }}
      onClick={() => {
        if (disabled) {
          return;
        }
        onClick();
      }}
      onMouseEnter={e => {
        if (!isSelected && !disabled) {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
        }
      }}
      onMouseLeave={e => {
        if (!isSelected && !disabled) {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }
      }}
    >
      <Flex
        direction={{ base: 'column', md: 'row' }}
        align={{ base: 'stretch', sm: 'center' }}
        justify='space-between'
        gap={{ base: 'sm', sm: 0 }}
        style={{ width: '100%' }}
      >
        <Flex
          w={{ base: '100%', sm: 'auto' }}
          mr={{ base: 0, sm: 'sm' }}
          mb={{ base: 'sm', sm: 0 }}
          justify='center'
          align='start'
        >
          {/* Icona */}
          <img
            src={icon}
            alt={title}
            style={{
              width: '60px',
              height: '60px',
              objectFit: 'contain',
              opacity: isSelected ? 1 : 0.5,
              filter: 'brightness(1.2)',
              transition: 'all 0.2s ease',
            }}
          />
        </Flex>

        <Flex flex={1} pl={{ base: 0, sm: 'md' }}>
          <Stack gap='0'>
            <Text size='xs' fw={400} c={isSelected ? 'black' : 'dimmed'}>
              Augmented Life {title === 'Esperienza' ? 'Experience' : 'Suite'}
            </Text>
            <Text fw={600} size='md' mt='xs' c={isSelected ? 'blue' : 'gray'}>
              Crea {title}
            </Text>

            <Text size='sm' c={isSelected ? 'gray.7' : 'dimmed'}>
              {description}
            </Text>

            {brandLogos && brandLogos.length > 0 && (
              <Group gap='xs' mt='xs'>
                {brandLogos.map((logo, index) => {
                  const logoUrl = typeof logo === 'string' ? logo : logo.url;
                  const logoName =
                    typeof logo === 'string' ? undefined : logo.name;
                  const isLogoActive =
                    typeof logo === 'string' ? true : logo.isActive;

                  const image = (
                    <Image
                      key={index}
                      src={logoUrl}
                      alt={logoName || 'Brand logo'}
                      w={20}
                      h={20}
                      fit='contain'
                      style={{
                        opacity: isLogoActive ? (isSelected ? 1 : 0.6) : 0.3,
                        filter: isLogoActive ? 'none' : 'grayscale(100%)',
                      }}
                    />
                  );

                  return logoName ? (
                    <Tooltip key={index} label={logoName} withArrow>
                      {image}
                    </Tooltip>
                  ) : (
                    image
                  );
                })}
              </Group>
            )}

            {disabledReason && disabled && (
              <Text size='xs' c='red' mt='xs'>
                {disabledReason}
              </Text>
            )}
            {footer && (
              <Box
                pt='xs'
                mt='xs'
                c={isSelected ? 'black' : 'dimmed'}
                style={{ borderTop: '1px solid #dee2e6' }}
              >
                {footer}
              </Box>
            )}
          </Stack>
        </Flex>
      </Flex>
    </Card>
  );
}
